import { Flex, HStack } from "@chakra-ui/react";

import AddressFromTo from "ui/shared/address/AddressFromTo";
import BlockEntity from "ui/shared/entities/block/BlockEntity";
import type { ChainConfig } from "types/multichain";
import DotoneCellLogo from "ui/shared/DotoneCellLogo";
import { Image } from "toolkit/chakra/image";
import ListItemMobile from "ui/shared/ListItemMobile/ListItemMobile";
import React from "react";
import { Skeleton } from "toolkit/chakra/skeleton";
import TimeWithTooltip from "ui/shared/time/TimeWithTooltip";
import type { Transaction } from "types/api/transaction";
import TxAdditionalInfo from "ui/txs/TxAdditionalInfo";
import TxEntity from "ui/shared/entities/tx/TxEntity";
import TxFee from "ui/shared/tx/TxFee";
import TxStatus from "ui/shared/statusTag/TxStatus";
import TxTranslationType from "./TxTranslationType";
import TxType from "ui/txs/TxType";
import TxWatchListTags from "ui/shared/tx/TxWatchListTags";
import config from "configs/app";
import { currencyUnits } from "lib/units";
import getValueWithUnit from "lib/getValueWithUnit";
import { isDotoneCellTransaction } from "tools/utils/checkDotoneCellAddress";
import { isMyDotAddress } from "tools/utils/checkMyDotContracts";
import { space } from "toolkit/utils/htmlEntities";

type Props = {
  tx: Transaction;
  showBlockInfo: boolean;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  animation?: string;
  chainData?: ChainConfig;
  timeTick?: number;
};

const TxsListItem = ({
  tx,
  isLoading,
  showBlockInfo,
  currentAddress,
  enableTimeIncrement,
  animation,
  chainData,
  timeTick,
}: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const isMyDotTx = isMyDotAddress(tx?.from?.hash);

  return (
    <ListItemMobile
      display="block"
      width="100%"
      animation={animation}
      key={tx.hash}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" mt={4}>
        <HStack flexWrap="wrap">
          {!isLoading && isDotoneCellTransaction(tx) && <DotoneCellLogo />}
          {tx.translation ? (
            <TxTranslationType
              types={tx.transaction_types}
              isLoading={isLoading || tx.translation.isLoading}
              translatationType={tx.translation.data?.type}
            />
          ) : (
            <TxType types={tx.transaction_types} isLoading={isLoading} />
          )}
          <TxStatus
            status={tx.status}
            errorText={tx.status === "error" ? tx.result : undefined}
            isLoading={isLoading}
          />
          <TxWatchListTags tx={tx} isLoading={isLoading} />
        </HStack>
        <TxAdditionalInfo tx={tx} isMobile isLoading={isLoading} />
      </Flex>
      <Flex
        justifyContent="space-between"
        lineHeight="24px"
        mt={3}
        alignItems="center"
      >
        <div className="flex h-full gap-1.5">
          {isMyDotTx && (
            <Image
              src="/assets/logo.svg"
              width={24}
              height={24}
              onClick={() =>
                window.open(
                  "https://mydot.one",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              alt="doto logo"
              className="mydot-transaction-logo w-5.5! h-5.5! cursor-pointer relative light-shadow-gold rounded-full"
            />
          )}
          <TxEntity
            isLoading={isLoading}
            hash={tx.hash}
            truncation="constant_long"
            fontWeight="700"
            icon={
              !tx.is_pending_update &&
              tx.transaction_types.includes("blob_transaction")
                ? { name: "blob" }
                : undefined
            }
            chain={chainData}
            isPendingUpdate={tx.is_pending_update}
          />
        </div>
        <TimeWithTooltip
          timestamp={tx.timestamp}
          enableIncrement={enableTimeIncrement}
          isLoading={isLoading}
          color="text.secondary"
          fontWeight="400"
          fontSize="sm"
          refreshTick={timeTick}
        />
      </Flex>
      {tx.method && (
        <Flex mt={3}>
          <Skeleton loading={isLoading} display="inline-block" whiteSpace="pre">
            Method{" "}
          </Skeleton>
          <Skeleton
            loading={isLoading}
            color="text.secondary"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            <span>{tx.method}</span>
          </Skeleton>
        </Flex>
      )}
      {showBlockInfo && tx.block_number !== null && (
        <Flex mt={2}>
          <Skeleton loading={isLoading} display="inline-block" whiteSpace="pre">
            Block{" "}
          </Skeleton>
          <BlockEntity isLoading={isLoading} number={tx.block_number} noIcon />
        </Flex>
      )}
      <AddressFromTo
        from={tx.from}
        to={dataTo}
        current={currentAddress}
        isLoading={isLoading}
        mt={6}
        fontWeight="500"
      />
      {!config.UI.views.tx.hiddenFields?.value && (
        <Flex mt={2} columnGap={2}>
          <Skeleton loading={isLoading} display="inline-block" whiteSpace="pre">
            Value
          </Skeleton>
          <Skeleton
            loading={isLoading}
            display="inline-block"
            color="text.secondary"
            whiteSpace="pre"
          >
            <span>
              {getValueWithUnit(tx.value).toFormat()}
              {space}
              {currencyUnits.ether}
            </span>
          </Skeleton>
        </Flex>
      )}
      {!config.UI.views.tx.hiddenFields?.tx_fee && (
        <Flex mt={2} mb={3} columnGap={2}>
          {(tx.stability_fee !== undefined || tx.fee.value !== null) && (
            <>
              <Skeleton
                loading={isLoading}
                display="inline-block"
                whiteSpace="pre"
              >
                Fee
              </Skeleton>
              <TxFee tx={tx} isLoading={isLoading} />
            </>
          )}
        </Flex>
      )}
    </ListItemMobile>
  );
};

export default React.memo(TxsListItem);
